import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaymentTypeSelector } from './PaymentTypeSelector';
import { StandardPayment } from './StandardPayment';
import { InstallmentPayment, Installment, Recipient } from './InstallmentPayment';
import { CardOnFilePayment } from './CardOnFilePayment';
import { PreAuthPayment } from './PreAuthPayment';
import { ShopProductsPayment } from './ShopProductsPayment';
import { FixedLinksPayment } from './FixedLinksPayment';
import { BulkPaymentUpload } from './BulkPaymentUpload';
import { BulkPaymentListSimplified } from './BulkPaymentListSimplified';
import { RecurringPayment } from './RecurringPayment';
import { InfoIcon, CheckCircleIcon, CalendarIcon, ArrowLeftIcon, ListIcon, RepeatIcon } from 'lucide-react';
import { ShareLinkModal } from './ShareLinkModal';
import { SuccessModal } from './SuccessModal';
export type PaymentType = 'standard' | 'installment' | 'card-on-file' | 'preauth' | 'shop-products' | 'fixed-links' | 'bulk' | 'recurring';
export type SubscriptionMode = 'existing' | 'new';
export function PaymentForm() {
  const navigate = useNavigate();
  const [paymentType, setPaymentType] = useState<PaymentType>('standard');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [subscriptionMode, setSubscriptionMode] = useState<SubscriptionMode>('existing');
  const [installmentData, setInstallmentData] = useState<Installment[]>([]);
  const [isMultipleRecipients, setIsMultipleRecipients] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryDays, setExpiryDays] = useState(30);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [reference, setReference] = useState('');
  const [securityType, setSecurityType] = useState<'public' | 'password' | 'email' | 'sms' | 'document' | 'geo'>('public');
  const [currentPage, setCurrentPage] = useState(1);
  const batchesPerPage = 10;
  // Calculate days between now and expiry date
  useEffect(() => {
    if (expiryDate) {
      const [month, day, year] = expiryDate.split('/').map(part => parseInt(part, 10));
      const expiryDateObj = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time part to get accurate day difference
      const timeDiff = expiryDateObj.getTime() - today.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      setExpiryDays(daysDiff);
    }
  }, [expiryDate]);
  // This is a mock function that would normally get data from the InstallmentPayment component
  // In a real app, you would use a ref or state lifting to get the actual data
  const getInstallmentData = () => {
    // For demonstration purposes, we're returning mock data
    if (paymentType === 'installment') {
      return [{
        id: 1,
        amount: '100.00',
        description: 'First payment',
        dueDate: '',
        recipient: {
          id: 1,
          email: 'recipient1@example.com',
          phone: '+1 (555) 123-4567'
        }
      }, {
        id: 2,
        amount: '200.00',
        description: 'Second payment',
        dueDate: '',
        recipient: {
          id: 2,
          email: 'recipient2@example.com',
          phone: '+1 (555) 987-6543'
        }
      }];
    }
    return [];
  };
  const handleButtonClick = () => {
    if (paymentType === 'card-on-file') {
      // Show success confirmation for card on file
      setShowSuccessModal(true);
    } else if (paymentType === 'bulk') {
      // Navigate to bulk payments page using React Router
      navigate('/bulk-payments');
    } else if (paymentType === 'recurring' && subscriptionMode === 'existing') {
      // Show success confirmation for existing customer subscription
      setShowSuccessModal(true);
    } else {
      // Get data based on payment type
      if (paymentType === 'installment') {
        const data = getInstallmentData();
        setInstallmentData(data);
        // Check if this is a split payment with multiple recipients
        // Only consider it a multi-recipient payment if we have recipients with contact info
        const hasValidRecipients = data.some(item => item.recipient && (item.recipient.email || item.recipient.phone));
        setIsMultipleRecipients(hasValidRecipients && data.length > 1);
      } else {
        // Reset installment data for other payment types
        setInstallmentData([]);
        setIsMultipleRecipients(false);
      }
      // Show share options modal
      setShowShareModal(true);
    }
  };
  const handleViewBatchDetail = (batchId: string) => {
    navigate(`/bulk-payment-detail/${batchId}`);
  };
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
  };
  const handleAmountChange = (newAmount: string) => {
    setAmount(newAmount);
  };
  const handleCurrencyChange = (newCurrency: string) => {
    setCurrency(newCurrency);
  };
  const handleReferenceChange = (newReference: string) => {
    setReference(newReference);
  };
  const handleSubscriptionModeChange = (mode: SubscriptionMode) => {
    setSubscriptionMode(mode);
  };
  const handleSecurityTypeChange = (newSecurityType: 'public' | 'password' | 'email' | 'sms' | 'document' | 'geo') => {
    setSecurityType(newSecurityType);
  };
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  // Generate payment summary based on current configuration
  const getPaymentSummary = () => {
    const paymentTypeLabels = {
      standard: 'single payment',
      installment: 'split payment',
      'card-on-file': 'saved card payment',
      preauth: 'security hold',
      'shop-products': 'product payment',
      'fixed-links': 'fixed link payment',
      bulk: 'bulk payment',
      recurring: 'recurring subscription'
    };
    const securityLabels = {
      public: 'public access',
      password: 'password protection',
      email: 'email verification',
      sms: 'SMS verification',
      document: 'document verification',
      geo: 'geographic restriction'
    };
    let summary = `Creating a ${paymentTypeLabels[paymentType]}`;
    if (amount) {
      summary += ` for $${amount} ${currency}`;
    }
    if (reference) {
      summary += ` with reference "${reference}"`;
    }
    if (securityType !== 'public') {
      summary += ` and ${securityLabels[securityType]} security`;
    }
    if (expiryDate && paymentType !== 'card-on-file' && paymentType !== 'preauth' && paymentType !== 'fixed-links' && paymentType !== 'recurring') {
      summary += ` that expires on ${expiryDate}`;
    }
    if (paymentType === 'fixed-links') {
      summary += ` that never expires`;
    }
    if (paymentType === 'recurring') {
      summary += ` that bills automatically on a recurring basis`;
    }
    return summary;
  };
  return <div className="space-y-6" data-id="element-1471">
      {/* Clean tabs without containing box */}
      <div className="border-b border-gray-200 pb-0" data-id="element-1472">
        <PaymentTypeSelector activeType={paymentType} onChange={setPaymentType} data-id="element-1473" />
      </div>
      <div className="mt-6" data-id="element-1474">
        {paymentType === 'standard' && <StandardPayment onExpiryDateChange={handleExpiryDateChange} onAmountChange={handleAmountChange} onCurrencyChange={handleCurrencyChange} onReferenceChange={handleReferenceChange} onSecurityTypeChange={handleSecurityTypeChange} data-id="element-1475" />}
        {paymentType === 'installment' && <InstallmentPayment onExpiryDateChange={handleExpiryDateChange} data-id="element-1476" />}
        {paymentType === 'card-on-file' && <CardOnFilePayment onExpiryDateChange={handleExpiryDateChange} onAmountChange={handleAmountChange} onCurrencyChange={handleCurrencyChange} onReferenceChange={handleReferenceChange} data-id="element-1477" />}
        {paymentType === 'preauth' && <PreAuthPayment onExpiryDateChange={handleExpiryDateChange} onAmountChange={handleAmountChange} onCurrencyChange={handleCurrencyChange} onReferenceChange={handleReferenceChange} data-id="element-1478" />}
        {paymentType === 'shop-products' && <ShopProductsPayment onExpiryDateChange={handleExpiryDateChange} data-id="element-1479" />}
        {paymentType === 'fixed-links' && <FixedLinksPayment onAmountChange={handleAmountChange} onCurrencyChange={handleCurrencyChange} onReferenceChange={handleReferenceChange} data-id="element-1480" />}
        {paymentType === 'recurring' && <RecurringPayment onAmountChange={handleAmountChange} onCurrencyChange={handleCurrencyChange} onReferenceChange={handleReferenceChange} onSubscriptionModeChange={handleSubscriptionModeChange} data-id="element-1481" />}
        {paymentType === 'bulk' && <div className="space-y-6" data-id="element-1482">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1483">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1484">
                Upload Payment Data
              </h3>
              <BulkPaymentUpload simplified={true} onBatchCreated={() => {}} data-id="element-1485" />
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1486">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1487">
                Recent Payment Batches
              </h3>
              <div className="mb-4 flex justify-between items-center" data-id="element-1488">
                <div className="text-sm text-gray-600" data-id="element-1489">
                  Showing the last {batchesPerPage} batches
                </div>
                <button onClick={() => navigate('/bulk-payments')} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors" data-id="element-1490">
                  View Full Batch History
                </button>
              </div>
              <BulkPaymentListSimplified onViewDetail={handleViewBatchDetail} data-id="element-1491" />
              {/* Simple pagination */}
              <div className="mt-4 flex justify-center" data-id="element-1492">
                <nav className="inline-flex rounded-md shadow-sm" data-id="element-1493">
                  <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1494">
                    Previous
                  </button>
                  <button onClick={() => handlePageChange(currentPage + 1)} className="px-3 py-1 rounded-r-md border border-gray-300 border-l-0 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-1495">
                    Next
                  </button>
                </nav>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1496">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1497">
                CSV Format Guidelines
              </h3>
              <div className="space-y-4" data-id="element-1498">
                <p className="text-sm text-gray-600" data-id="element-1499">
                  Your CSV file should include the following columns:
                </p>
                <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-1500">
                  <table className="min-w-full divide-y divide-gray-200" data-id="element-1501">
                    <thead className="bg-gray-50" data-id="element-1502">
                      <tr data-id="element-1503">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1504">
                          Column
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1505">
                          Required
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1506">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200" data-id="element-1507">
                      <tr data-id="element-1508">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1509">
                          customerName
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-1510">Yes</td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1511">
                          Customer's full name
                        </td>
                      </tr>
                      <tr data-id="element-1512">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1513">
                          email
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-1514">Yes</td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1515">
                          Customer's email address
                        </td>
                      </tr>
                      <tr data-id="element-1516">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1517">
                          phone
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-1518">No</td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1519">
                          Customer's phone number
                        </td>
                      </tr>
                      <tr data-id="element-1520">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1521">
                          amount
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-1522">Yes</td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1523">
                          Payment amount (numeric only)
                        </td>
                      </tr>
                      <tr data-id="element-1524">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1525">
                          currency
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-1526">No</td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1527">
                          Currency code (USD, EUR, etc.)
                        </td>
                      </tr>
                      <tr data-id="element-1528">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1529">
                          description
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-1530">Yes</td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1531">
                          Payment description
                        </td>
                      </tr>
                      <tr data-id="element-1532">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1533">
                          reference
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-1534">No</td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1535">
                          Your internal reference
                        </td>
                      </tr>
                      <tr data-id="element-1536">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1537">
                          dueDate
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-1538">No</td>
                        <td className="px-4 py-3 text-sm text-gray-500" data-id="element-1539">
                          Due date (MM/DD/YYYY)
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-gray-600" data-id="element-1540">
                  The first row of your CSV should contain these column headers
                  exactly as shown above.
                </p>
              </div>
            </div>
          </div>}
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex justify-between items-center" data-id="element-1541">
        <div className="flex items-start text-sm text-gray-600" data-id="element-1542">
          {paymentType === 'card-on-file' ? <div className="flex items-center" data-id="element-1543">
              <InfoIcon size={16} className="mr-2 text-blue-600 flex-shrink-0" data-id="element-1544" />
              <span data-id="element-1545">The customer will be charged immediately</span>
            </div> : paymentType === 'preauth' ? <div className="flex items-center" data-id="element-1546">
              <InfoIcon size={16} className="mr-2 text-blue-600 flex-shrink-0" data-id="element-1547" />
              <span data-id="element-1548">
                The customer will see a temporary authorization on their card
              </span>
            </div> : paymentType === 'bulk' ? <div className="flex items-center" data-id="element-1549">
              <InfoIcon size={16} className="mr-2 text-blue-600 flex-shrink-0" data-id="element-1550" />
              <span data-id="element-1551">
                Create multiple payment requests at once by uploading a CSV file
              </span>
            </div> : paymentType === 'recurring' ? <div className="flex items-center" data-id="element-1552">
              <InfoIcon size={16} className="mr-2 text-blue-600 flex-shrink-0" data-id="element-1553" />
              <span data-id="element-1554">
                {subscriptionMode === 'existing' ? 'Set up automatic recurring payments for an existing customer' : 'Send a subscription link to start a recurring payment'}
              </span>
            </div> : <div className="flex" data-id="element-1555">
              <InfoIcon size={16} className="mr-2 text-blue-600 mt-0.5 flex-shrink-0" data-id="element-1556" />
              <span data-id="element-1557">{getPaymentSummary()}</span>
            </div>}
        </div>
        {paymentType === 'bulk' ? <button className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors" onClick={handleButtonClick} data-id="element-1558">
            Continue to bulk upload
          </button> : <button className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors" onClick={handleButtonClick} data-id="element-1559">
            {paymentType === 'card-on-file' ? 'Process charge' : paymentType === 'preauth' ? 'Create pre-authorization' : paymentType === 'recurring' ? subscriptionMode === 'existing' ? 'Create subscription' : 'Create subscription link' : 'Create payment link'}
          </button>}
      </div>
      {/* Share Link Modal */}
      {showShareModal && <ShareLinkModal onClose={() => setShowShareModal(false)} recipients={isMultipleRecipients ? installmentData.map(item => item.recipient).filter((recipient): recipient is Recipient => Boolean(recipient && (recipient.email || recipient.phone))) : []} data-id="element-1560" />}
      {/* Success Modal for Card on File and Existing Customer Subscription */}
      {showSuccessModal && <SuccessModal onClose={() => setShowSuccessModal(false)} title={paymentType === 'recurring' ? 'Subscription Created' : 'Payment Processed'} message={paymentType === 'recurring' ? 'The subscription has been successfully created and the customer will be billed according to the schedule.' : "The customer's card has been successfully charged."} data-id="element-1561" />}
    </div>;
}